import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  Platform,
  getInstallState,
  registerBeforeInstallPromptListener,
  promptPWAInstall,
  trackCTAClick,
  trackInstallPromptShown,
  trackInstallAccepted,
  trackInstallDismissed,
  trackIOSModalOpened
} from '../utils/pwaInstall.js';

const GetTheAppCTA = ({ className = '', style = {} }) => {
  const [installState, setInstallState] = useState(Platform.NOT_INSTALLABLE);
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    // Check initial state
    setInstallState(getInstallState());

    // Register for install prompt availability
    const cleanup = registerBeforeInstallPromptListener(() => {
      setInstallState(getInstallState());
    });

    return cleanup;
  }, []);

  // Don't show CTA inside the native app, when already installed, or when not installable
  if (Capacitor.isNativePlatform() || installState === Platform.NOT_INSTALLABLE || installState === Platform.INSTALLED) {
    return null;
  }

  const getMicrocopy = () => {
    switch (installState) {
      case Platform.INSTALLED:
        return 'Pista is installed';
      case Platform.IOS:
        return 'Add Pista to your Home Screen';
      case Platform.INSTALLABLE:
        return 'Install Pista for the best experience';
      case Platform.NOT_INSTALLABLE:
      default:
        return 'Use Pista in your browser';
    }
  };

  const handleCTAClick = async () => {
    trackCTAClick(installState);

    switch (installState) {
      case Platform.IOS:
        setShowIOSModal(true);
        trackIOSModalOpened();
        break;

      case Platform.INSTALLABLE:
        trackInstallPromptShown();
        const result = await promptPWAInstall();
        if (result === 'accepted') {
          trackInstallAccepted();
        } else if (result === 'dismissed') {
          trackInstallDismissed();
        }
        break;

      case Platform.INSTALLED:
        // Do nothing for installed state
        break;

      case Platform.NOT_INSTALLABLE:
      default:
        window.location.href = process.env.REACT_APP_FRONTEND_URL || 'https://app.voyagerbug.com';
        break;
    }
  };

  const isButtonDisabled = installState === Platform.INSTALLED;

  return (
    <>
      <div className={`get-the-app-cta ${className}`} style={style}>
        <button
          onClick={handleCTAClick}
          disabled={isButtonDisabled}
          className="get-the-app-button"
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            borderRadius: '8px',
            border: 'none',
            cursor: isButtonDisabled ? 'default' : 'pointer',
            backgroundColor: isButtonDisabled ? 'var(--bg-tertiary, #e5e7eb)' : 'var(--pista-green-primary, #10b981)',
            color: isButtonDisabled ? 'var(--text-secondary, #9ca3af)' : 'var(--pista-button-text, #ffffff)',
            transition: 'all 0.2s ease',
            width: '100%',
            maxWidth: '300px'
          }}
        >
          Get the App
        </button>
        <p
          className="get-the-app-microcopy"
          style={{
            margin: '8px 0 0 0',
            fontSize: '14px',
            color: 'var(--text-secondary, #6b7280)',
            textAlign: 'center',
            lineHeight: '1.4'
          }}
        >
          {getMicrocopy()}
        </p>
      </div>

      {/* iOS Install Modal */}
      {showIOSModal && (
        <div
          className="ios-install-modal-overlay"
          onClick={() => setShowIOSModal(false)}
        >
          <div
            className="ios-install-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="ios-modal-title">
              Install Pista on iPhone/iPad
            </h3>
            <ol className="ios-modal-steps">
              <li className="ios-modal-step">Open in Safari</li>
              <li className="ios-modal-step">
                Tap Share (square with arrow)
              </li>
              <li className="ios-modal-step">Tap &quot;Add to Home Screen&quot;</li>
            </ol>
            <button
              onClick={() => setShowIOSModal(false)}
              className="ios-modal-button"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GetTheAppCTA;
